import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Info } from 'lucide-react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@muzly/ui-kit';
import { breathStabilityScore } from '@muzly/voice-engine';

import { BreathSection } from './BreathSection';
import { FatigueSection } from './FatigueSection';
import { PitchRegisterSection } from './PitchRegisterSection';
import { RangeSection } from './RangeSection';
import { ResonanceStyleSection } from './ResonanceStyleSection';
import { SingersFormantSection } from './SingersFormantSection';
import { VibratoSection } from './VibratoSection';
import {
  AlertCircleIcon,
  PAGE_HEADER_STICKY_TOP,
  ScoreHero,
} from '../../../../components';
import { conditionScore } from '../../fatigueRadar';
import { GRADE_LABEL_KEY, scoredMetrics } from '../../metricGrades';
import { tabInsight } from '../../resultCopy';

import type { MetricGrade, ScoredMetricKey } from '../../metricGrades';
import type { DiagnosisRecord } from '../../record';
import type { MetricTabId } from '../../resultCopy';

/**
 * 지표 상세의 공통 셸 — 탭 바 · 탭 머리(제목·부제·큰 점수) · 본문 · 한 줄 해석 · 각주
 * (시안 §8.2).
 *
 * 지표를 쌓지 않고 탭으로 미는 이유: 한 지표를 볼 때 다른 지표의 숫자가 같이 보이면
 * 사람은 그것들을 서로 견준다. 그런데 이 값들은 **서로 다른 자로 잰 것**이라(호흡은 반음
 * 대비 편차, 음역 구간은 이웃 표본 간격의 퍼짐) 82와 71을 나란히 두면 없는 비교가 생긴다.
 *
 * **탭 일곱이 시안 §8.2의 목록이다**(목소리 피로도·음역·음정·바이브레이션·호흡 안정도·
 * 싱어즈 포먼트·공명 구간). 한 회차가 그 일곱을 다 세우지는 않는다 — 그릴 값이 하나도
 * 없는 탭은 안 선다(`visibleTabs`). 한때는 다 세우고 값 자리에 `측정 준비 중`을 그렸는데,
 * 눌러도 아무것도 없는 탭이 절반이었고 그 문구는 App Store 심사가 보는 미완 표식이었다.
 * 값이 **일부만** 없는 탭은 그대로 선다 — 그 칸만 빈 상태 문구다.
 *
 * 어느 탭을 세울지는 **호출부가 정한다**(`only`). 둘 다 `visibleTabs`를 근거로 부르고,
 * 진단 직후의 결과 본문은 거기서 음역·피로도를 더 뺀다(자기 섹션으로 이미 그린다).
 */

/**
 * 탭 하나가 그리는 것. `Record<MetricTabId, …>`인 이유: 지표를 하나 더하면
 * (`METRIC_TAB_IDS`) **여기서 컴파일이 깨진다** — 목록에만 있고 그릴 것이 없는 탭이
 * 조용히 서는 일을 타입이 막는다.
 */
const TAB_RENDER: Record<
  MetricTabId,
  (
    r: DiagnosisRecord,
    previous: DiagnosisRecord | null,
    history: readonly DiagnosisRecord[],
  ) => ReactElement
> = {
  // 피로도만 이력을 본다 — 축이 이 회차가 아니라 컨디션별 이력에서 나온다.
  fatigue: (_record, _previous, history) => (
    <FatigueSection history={history} />
  ),
  range: (record) => <RangeSection record={record} />,
  registers: (record, previous) => (
    <PitchRegisterSection record={record} previous={previous} />
  ),
  vibrato: (record) => <VibratoSection record={record} />,
  breath: (record) => <BreathSection record={record} />,
  formant: (record) => <SingersFormantSection record={record} />,
  resonance: (record) => <ResonanceStyleSection record={record} />,
};

/**
 * 탭 머리의 큰 점수(시안 `87 / 100`). **점수 축이 있는 탭만** 낸다 — 음역·싱어즈 포먼트·
 * 공명 구간은 사실(음이름·Hz)이지 점수가 아니라 자리를 비운다(`undefined`). 피로도는 이
 * 회차가 답한 컨디션의 축 값이고(`conditionScore`), 홈 타일이 **같은 함수**를 불러 두
 * 화면이 같은 숫자를 말한다. 호흡은 **아래 구간 점수의 평균**이다 —
 * 예전엔 `sustain`(지속 시간, 12초 만점)을 냈는데 구간 점수(흔들림)와 자가 달라 84·79 위에
 * 32가 섰다(TestFlight 3차 #108). 홈 타일도 같은 함수를 쓴다.
 */
const TAB_SCORE: Record<
  MetricTabId,
  (
    r: DiagnosisRecord,
    history: readonly DiagnosisRecord[],
  ) => number | null | undefined
> = {
  fatigue: conditionScore,
  range: () => undefined,
  registers: (r) =>
    r.scores.find((s) => s.key === 'pitchAccuracy')?.val ?? null,
  vibrato: (r) => r.scores.find((s) => s.key === 'vibrato')?.val ?? null,
  breath: (r) => breathStabilityScore(r.breath ?? []),
  formant: () => undefined,
  resonance: () => undefined,
};

/**
 * 화면에 서는 순서 — 시안 §8.2의 탭 바 그대로다. 홈 타일(`homeTiles`)은 **다른 순서**를
 * 쓴다: 시안 §7.1이 `음정`을 `음역`보다 앞에 둔다. 순서를 공유 상수로 굳히면 한 화면이
 * 시안과 어긋나므로 각자 갖는다.
 */
const TAB_ORDER: MetricTabId[] = [
  'fatigue',
  'range',
  'registers',
  'vibrato',
  'breath',
  'formant',
  'resonance',
];

/**
 * 탭 → 그 탭 점수가 어느 축에서 왔나. 근거 등급(`견고`/`간접 지표`/`근거 약함`)은
 * 제품의 주장 자체인데, 예전에는 진단 직후 화면(`SummarySection`)에만 붙어 있어
 * **다시 열 수 없는 화면에서만** 보였다. 점수를 내는 탭에는 여기서도 붙인다.
 * 점수가 없는 탭(음역·포먼트·공명)은 사실을 그리는 자리라 등급이 없다.
 */
const TAB_GRADE_SOURCE: Partial<Record<MetricTabId, ScoredMetricKey>> = {
  registers: 'pitchAccuracy',
  vibrato: 'vibrato',
  // 구간 점수는 `pitchStability`와 같은 식(음높이 흔들림)이라 등급도 그 축을 따른다.
  breath: 'pitchStability',
};

const GRADE_STYLE: Record<MetricGrade, string> = {
  solid: 'bg-primary/15 text-primary',
  proxy: 'bg-muted text-muted-foreground',
  weak: 'border border-dashed border-border text-muted-foreground',
};

/** 이 탭 점수의 근거 등급 카드. 점수를 안 내는 탭은 없다. */
const gradeCard = (tab: MetricTabId) => {
  const key = TAB_GRADE_SOURCE[tab];
  return key
    ? scoredMetrics().find((entry) => entry.key === key)?.card
    : undefined;
};

/**
 * 근거 등급 배지 — **누르면 그 등급의 이유가 펼쳐진다**.
 *
 * 이유를 `title` 속성에만 두던 때는 터치(WebView)에서 아무것도 뜨지 않았다. "이 점수를
 * 믿어도 되나"는 이 화면에 들어온 이유 자체라(PRODUCT 원칙 1), 누를 수 있어야 하고 눌렀을
 * 때 문장이 화면에 있어야 한다. 배지는 작게 두고 누르는 자리만 44px 로 넓힌다.
 */
const GradePill = ({
  grade,
  open,
  noteId,
  onToggle,
}: {
  grade: MetricGrade;
  open: boolean;
  noteId: string;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={noteId}
      className={`relative inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium after:absolute after:-inset-x-1 after:-inset-y-3 after:content-[''] ${GRADE_STYLE[grade]}`}
    >
      {t(GRADE_LABEL_KEY[grade])}
      <Info className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
};

/** 한 줄 해석 카드 — 시안 §8.2의 연분홍 카드(제목 18 + 핑크 본문). 할 말이 없으면 안 그린다. */
const InsightCard = ({
  record,
  tab,
}: {
  record: DiagnosisRecord;
  tab: MetricTabId;
}) => {
  const { t } = useTranslation();
  const key = tabInsight(record, tab);
  if (key === null) {
    return null;
  }
  return (
    <div className="mx-[18px] rounded-[24px] bg-primary/[0.03] px-5 py-[18px]">
      <h3 className="text-lg font-semibold tracking-[-0.09px] text-bk-900">
        {t('web.result.tabs.insightTitle')}
      </h3>
      <p className="mt-2 text-sm leading-[1.35] tracking-[-0.07px] text-pink">
        {t(`web.result.tabs.insight.${tab}.${key}`)}
      </p>
    </div>
  );
};

export const ResultTabs = ({
  record,
  previous,
  history = [],
  only,
  tab,
  onTabChange,
}: {
  record: DiagnosisRecord;
  /** 이력 대비의 기준선(직전 회차). 첫 회차에는 null. */
  previous: DiagnosisRecord | null;
  /** 저장된 회차 전부. 피로도 탭만 쓴다 — 안 주면 그 탭은 값이 없다. */
  history?: readonly DiagnosisRecord[];
  /** 세울 탭 목록 — 값이 있는 탭만(`visibleTabs`). 이 순서가 아니라 `TAB_ORDER` 로 선다. */
  only: MetricTabId[];
  /**
   * 지금 탭 — 주면 **주소가 정본**이 된다(`/result/:id/:tab`). 안 주면 내부 상태로 돈다.
   * 두 경로를 한 컴포넌트가 갖는 이유: 탭 바·해석 카드·각주가 같은 셸이라, 갈라 놓으면
   * 시안 §8.2가 두 벌이 된다.
   */
  tab?: MetricTabId;
  onTabChange?: (id: MetricTabId) => void;
}) => {
  const { t } = useTranslation();
  const tabs = TAB_ORDER.filter((id) => only.includes(id));
  const [active, setActive] = useState<MetricTabId | null>(null);
  // 근거 설명을 펼친 탭 — 탭을 바꾸면 접힌다(다른 탭의 이유가 따라오지 않게).
  const [gradeOpenFor, setGradeOpenFor] = useState<MetricTabId | null>(null);
  // 오른쪽에 더 있나 — 페이드를 띄울지 정한다. 탭 수·글자 길이가 언어마다 달라서
  // 실제로 측정해야 안다(한국어에선 넘치고 영어에선 안 넘칠 수 있다).
  const listRef = useRef<HTMLDivElement | null>(null);
  const [more, setMore] = useState(false);
  const measure = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      return;
    }
    setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  const onListScroll = measure;
  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);
  // 고른 탭이 스트립 밖이면 끌어온다 — 이력에서 특정 축으로 바로 들어오면 활성 탭이
  // 스크롤 밖에 서 있어, 어느 탭을 보고 있는지 탭 바가 말해 주지 못했다.
  //
  // **스트립의 가로 스크롤만 건드린다.** `scrollIntoView({block:'nearest'})` 는 세로 축도
  // 같이 맞추는데, 결과 화면에서 이 스트립은 음역 섹션 아래 = 첫 화면 밖이라 마운트하자마자
  // 문서가 통째로 내려갔다(결과가 중간 스크롤에서 열렸다 — DEBUG-16-02-00). 겹친 만큼만
  // `scrollLeft` 를 미는 것이 `inline: 'nearest'` 와 같은 계산이고, 문서는 건드리지 않는다.
  useEffect(() => {
    const list = listRef.current;
    const el = list?.querySelector('[data-state="active"]');
    if (!list || !(el instanceof HTMLElement)) {
      return;
    }
    const strip = list.getBoundingClientRect();
    const trigger = el.getBoundingClientRect();
    const overRight = trigger.right - strip.right;
    const overLeft = trigger.left - strip.left;
    if (overRight > 0) {
      list.scrollLeft += overRight;
    } else if (overLeft < 0) {
      list.scrollLeft += overLeft;
    }
  }, [active, tab]);
  /**
   * 탭을 안 고른 채 들어왔을 때 설 자리 — **첫 탭**이다. `only`(`visibleTabs`)가 이미 값
   * 있는 탭만 담는다. 예전에는 여기서 `hasMetric` 을 한 번 더 걸렀는데, 피로도는 회차가
   * 아니라 이력에서 값이 나오므로(`hasMetric('fatigue')` 는 늘 false) 탭 순서 첫째인
   * 피로도가 기본 탭이 될 수 없었다 — 홈 타일은 피로도로 가는데 이력 카드는 음역으로 갔다.
   */
  const fallback = tabs[0];
  // 모르는 탭 이름(주소를 손으로 고쳤다)도 같은 자리로 떨어진다 — 빈 화면을 보이지 않는다.
  const current = tabs.find((id) => id === (tab ?? active)) ?? fallback;
  const score = TAB_SCORE[current](record, history);
  const grade = gradeCard(current);
  const gradeOpen = gradeOpenFor === current;
  const gradeNoteId = `grade-note-${current}`;

  return (
    <section className="flex flex-col gap-4">
      {/* 탭 바는 ui-kit(Radix)이 그린다 — 화살표 키 이동·roving tabindex·`aria-controls`가
          손으로 짠 `role="tab"` 버튼에는 없었다. 시안 §8.2의 모양(라운드 10, 활성만 연분홍
          바탕 + 레드 글자, 비활성은 바탕 없이 회색 글자)은 className으로 입힌다. */}
      <Tabs
        value={current}
        onValueChange={(id) => {
          setActive(id as MetricTabId);
          onTabChange?.(id as MetricTabId);
        }}
        className="flex flex-col gap-2"
      >
        {/* 가로 스크롤 — 탭이 늘어도 줄바꿈이 아니라 옆으로 흐른다(시안 §8.2).
            일곱 탭은 ~660px 이고 화면은 354px 이라 **뒤 세 탭은 보이지도, 있는 줄도
            모른다**(2026-09-16 실기기). 스크롤바는 숨겨져 있으니 넘침을 말하는 것은
            오른쪽 페이드뿐이다 — 끝까지 밀면 사라지도록 스크롤 상태를 따라간다. */}
        {/* 상단 바 바로 밑에 붙어 선다 — 이 스트립이 이 화면의 주 내비게이션이고,
            일곱 탭이 가로로 흐르는 탓에 스크롤에 실려 올라가면 탭을 바꾸려 본문을 맨 위까지
            되감아야 한다. `top` 은 바가 차지하는 높이(`PAGE_HEADER_STICKY_TOP`)이고,
            `z-20` 은 바(z-30) **아래**라 겹칠 때 스트립이 바를 덮지 않는다. 바탕을 칠해야
            본문이 비쳐 흐르지 않는다(오른쪽 페이드도 같은 `background` 로 녹는다). */}
        <div
          className="sticky z-20 bg-background"
          style={{ top: PAGE_HEADER_STICKY_TOP }}
        >
          <TabsList
            ref={listRef}
            onScroll={onListScroll}
            aria-label={t('web.result.tabs.label')}
            className="h-auto w-full justify-start gap-0 overflow-x-auto rounded-none bg-transparent p-0 px-[18px] py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {tabs.map((id) => (
              <TabsTrigger
                key={id}
                value={id}
                className="min-h-[44px] shrink-0 rounded-[10px] px-3 py-2.5 text-[15px] font-medium tracking-[-0.075px] text-bk-600 transition-colors data-[state=active]:bg-primary/[0.04] data-[state=active]:font-semibold data-[state=active]:text-primary data-[state=active]:shadow-none"
              >
                {t(`web.result.tabs.name.${id}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {more && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background to-transparent"
            />
          )}
        </div>

        {/* 탭 머리 — 제목(24) · 부제(14) · 큰 점수. 탭마다 같은 틀이라 본문 밖에서 한 번 그린다. */}
        <div className="flex flex-col">
          <div className="flex flex-col gap-2 px-[18px] py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold tracking-[-0.12px] text-black">
                {t(`web.result.tabs.name.${current}`)}
              </h2>
              {grade && (
                <GradePill
                  grade={grade.grade}
                  open={gradeOpen}
                  noteId={gradeNoteId}
                  onToggle={() => setGradeOpenFor(gradeOpen ? null : current)}
                />
              )}
            </div>
            <p className="text-sm font-medium leading-normal tracking-[-0.07px] text-bk-700">
              {t(`web.result.tabs.subtitle.${current}`)}
            </p>
            {/* 접혀 있어도 DOM 에 둔다 — 배지의 `aria-controls` 가 가리킬 자리가 늘 있어야 한다. */}
            {grade && (
              <p
                id={gradeNoteId}
                hidden={!gradeOpen}
                className="rounded-xl bg-bk-50 px-3 py-2.5 text-[13px] leading-relaxed text-bk-700"
              >
                {t(grade.noteKey)}
              </p>
            )}
          </div>
          {score !== undefined && (
            <div className="px-[18px] py-3.5">
              <ScoreHero score={score} />
            </div>
          )}
        </div>

        {tabs.map((id) => (
          <TabsContent key={id} value={id} className="mt-0">
            {TAB_RENDER[id](record, previous, history)}
          </TabsContent>
        ))}
      </Tabs>

      <InsightCard record={record} tab={current} />

      <div className="flex flex-col items-center gap-3 px-[18px] py-2.5">
        <AlertCircleIcon className="h-6 w-6 text-bk-400" aria-hidden="true" />
        <p className="text-center text-sm tracking-[-0.07px] text-bk-600">
          {t('web.result.tabs.footnote')}
        </p>
      </div>
    </section>
  );
};
